import React from "react";
import Leaflet from "leaflet";
import {
  compose,
  getContext,
  createSink,
  mapProps,
  withHandlers,
  withPropsOnChange
} from "recompose";
import { connect } from "react-redux";
import {
  Map,
  GeoJSON,
  WMSTileLayer,
  LayersControl,
  TileLayer,
  PropTypes,
  Popup,
  LayerGroup
} from "react-leaflet";
import ExtraTileLayer from "./ExtraTileLayer";
import topicBorder from "./topicBorder";
import PolygonEditControlGroup from "./PolygonEditControlGroup";
import LineEditControlGroup from "./LineEditControlGroup";
import "proj4leaflet";
import "leaflet/dist/leaflet.css";

Leaflet.Icon.Default.imagePath = "/leaflet/";

const objectsToGeoJSON = proposal => {
  const { objects } = proposal.content || {};
  return (objects || []).map(object => ({
    type: "Feature",
    properties: {
      style: {},
      context: { proposal, object },
      id: object.id,
      key: object.id + object.location.coordinates.toString()
    },
    geometry: object.location
  }));
};

const styleFromFeature = feature => {
  return feature.properties.style;
};

const withMap = getContext({ map: PropTypes.map });

const withFeatureHandler = compose(
  withMap,
  withHandlers({
    handleFeature: props => (feature, layer) => {
      const { map, onClick } = props;
      const {
        properties: { context }
      } = feature;
      layer.on("click", event => onClick(event, { context, map }));
    }
  })
);

const withObject = compose(
  mapProps(props => {
    const { object, proposal } = props;
    return {
      title: object.id,
      ...object,
      object,
      proposal
    };
  })
);

const ObjectPopup = withObject(props => {
  const { title, type, inputs } = props;
  return (
    <Popup>
      <div>
        <strong>
          {title} <br />
        </strong>
        <div>type: {type}</div>
        {Object.keys(inputs || {}).map(key => (
          <div key={key}>
            {key}: {inputs[key]}
          </div>
        ))}
      </div>
    </Popup>
  );
});

const ProposalList = withFeatureHandler(props => {
  const { proposals, handleFeature } = props;
  return (proposals || []).map(proposal => {
    return (
      <LayerGroup key={proposal._id}>
        {objectsToGeoJSON(proposal).map((data, key) => {
          return (
            <GeoJSON
              data={data}
              style={styleFromFeature}
              key={data.properties.key}
              onEachFeature={handleFeature}
            >
              <ObjectPopup {...data.properties.context} />
            </GeoJSON>
          );
        })}
      </LayerGroup>
    );
  });
});

const Layer = props => {
  const { type, urlTemplate, baseURL, options } = props;
  switch (type) {
    case "TileLayer":
      return <TileLayer {...options} url={urlTemplate} />;
    case "TileLayer.WMS":
      return <WMSTileLayer {...options} url={baseURL} />;
    default:
      return null;
  }
};

const Layers = props => {
  const { baseLayers, overlays, options, children, extraTiles = [] } = props;
  return (
    <LayersControl {...options}>
      {baseLayers.map(({ layer, checked }, key) => (
        <LayersControl.BaseLayer name={layer.title} checked={checked} key={key}>
          <Layer {...layer} />
        </LayersControl.BaseLayer>
      ))}
      {extraTiles.map((name, key) => (
        <LayersControl.BaseLayer name={name} key={key}>
          <ExtraTileLayer name={name} />
        </LayersControl.BaseLayer>
      ))}
      {overlays.map(({ layer, checked }, key) => (
        <LayersControl.Overlay name={layer.title} checked={checked} key={key}>
          <Layer {...layer} />
        </LayersControl.Overlay>
      ))}
      {children}
    </LayersControl>
  );
};

const withSizeHook = compose(
  connect(state => ({
    sidebar: state.layout.sidebar
  })),
  withPropsOnChange(["sidebar"], () => ({
    invalidateSize: true
  }))
);

const MapSink = compose(
  withSizeHook,
  withMap
)(
  createSink(props => {
    const { invalidateSize = false, map } = props;
    if (invalidateSize) {
      map.invalidateSize();
    }
  })
);

const withSelectedProposals = Map => props => {
  const {
    options,
    onMouseDown,
    onClick,
    onObjectClick,
    proposals,
    areaOfInterest,
    layersControl,
    bounds,
    buildings,
    buildingsStyle,
    mode,
    onEachBuilding
  } = props;

  const coords = areaOfInterest.coordinates[0];
  const maxBounds = Leaflet.GeoJSON.coordsToLatLngs(coords);
  const extraTiles = ["Esri.WorldImagery", "Stamen.Watercolor"];
  return (
    <Map
      {...options}
      onMouseDown={onMouseDown}
      bounds={bounds || maxBounds}
      maxBounds={maxBounds}
    >
      <MapSink />
      <Layers {...layersControl} extraTiles={extraTiles}>
        <LayersControl.Overlay name="Area of interest" checked>
          <GeoJSON
            className="area-of-interest"
            data={topicBorder(areaOfInterest)}
            style={styleFromFeature}
            onClick={!buildings ? onClick : undefined}
          />
        </LayersControl.Overlay>
      </Layers>
      <ProposalList proposals={proposals} onClick={onObjectClick} />
      {mode === "polygon" && <PolygonEditControlGroup />}
      {mode === "line" && <LineEditControlGroup />}
      {buildings && (
        <GeoJSON
          className="buildings"
          data={buildings}
          style={buildingsStyle}
          onEachFeature={onEachBuilding}
        />
      )}
    </Map>
  );
};

const selectMapState = state => ({
  bounds: state.map.settings.bounds
});

export default compose(
  connect(selectMapState),
  withHandlers({
    onEachBuilding: props => (feature, layer) => {
      const { onClick } = props;
      layer.on("click", onClick);
    },
    buildingsStyle: props => feature => {
      return {
        stroke: false,
        fill: true,
        fillOpacity: 0,
        color: feature.properties.fill
      };
    }
  }),
  withSelectedProposals
)(Map);



// WEBPACK FOOTER //
// ./src/app/map/Map.js